import React, { useState, useEffect, Suspense, useRef } from 'react';
import api from './api/api'; 
import Sidebar from './Sidebar'; 
import MainMenu from './MainMenu';
import ChatSidebar from './ChatSidebar';  
import ChatContainer from './ChatContainer';  
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import SidebarDisplayButton from './SidebarDisplayButton';
import Loading from './Loading';
import { v4 as uuidv4 } from 'uuid';
import ConfirmDialog from './utils/ConfirmDialog';
import { useDashboardContext } from '../context/DashboardContext';
import { saveAccessLog } from './utils/commonUtils';
import DashboardModal from './dashboard/DashboardModal';


function ChatPage() {
    const {chatId} = useParams();
    const [utteranceFromApi, setUtteranceFromApi] = useState([]);
    const { state, dispatch } = useDashboardContext(); // Contextから取得
    const [selectedChatId, setSelectedChatId] = useState(null);
    const [showChatSidebar, setShowChatSidebar] = useState(true);
    const [navigationExamples, setNavigationExamples] = useState([]);
    const [showDeleteOkDialog, setShowDeleteOkDialog] = useState(false);
    const [deleteChat, setDeleteChat] = useState(null);
    const [renameChat, setRenameChat] = useState(null);
    const [showRenameDialog, setShowRenameDialog] = useState(false);
    const [chats, setChats] = useState([]);
    const [showDashboard, setShowDashboard] = useState(false);
    const [secCode, setSecCode] = useState(null); // ダッシュボードの証券コード
    const [showMenu, setShowMenu] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const sidebarRef = useRef(null);
    const chatSidebarRef = useRef(null);
    const mainMenuRef = useRef(null); 

    const handleOutsideClick = (event) => {
        if (
            (sidebarRef.current && sidebarRef.current.contains(event.target)) ||
            (chatSidebarRef.current && chatSidebarRef.current.contains(event.target)) ||
            (mainMenuRef.current && mainMenuRef.current.contains(event.target)) // ← MainMenu クリックを除外
        ) {
            return;
        }

        setShowMenu(false);
    };

    useEffect(() => {
        document.addEventListener('click', handleOutsideClick);
        document.addEventListener('touchstart', handleOutsideClick);

        return () => {
            document.removeEventListener('click', handleOutsideClick);
            document.removeEventListener('touchstart', handleOutsideClick);
        };
    }, []);

    useEffect(() => {
        if (chatId) {
          setSelectedChatId(chatId);
          fetchChatData(chatId);
        } else {
          const newId = uuidv4();
          setSelectedChatId(newId);
          navigate(`/chat/${newId}`, { replace: true });
        }
        saveAccessLog('chat page');
    }, [chatId]);

    const handleDashboardLinkClick = (event) => {
        if (event.target.classList.contains('dashboard-link')) {
          event.preventDefault();
          const clickedSecCode = event.target.getAttribute('data-sec-code');
          setSecCode(clickedSecCode);
            //   setDashboardSecCode(clickedSecCode); 
          dispatch({ type: 'SET_SEC_CODE', payload: clickedSecCode})
          setShowDashboard(true);
        }
      };

    const fetchChatData = async (chatId) => {
        try {
            const response = await api.get(`${process.env.REACT_APP_API_URL}/chat/api/chat/${chatId}/`, {
            }).then(res => { setUtteranceFromApi(res.data) })
        } catch (error) {
            if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                // 未認証の場合、ログイン画面にリダイレクト
                // navigate('/login', { state: { from: location.pathname } });
            } else {
                console.error('Error fetching chat:', error);
            }
        }
    }
  
    const fetchNavigationExamples = async () => {
        try {
            const response = await api.get(`${process.env.REACT_APP_API_URL}/chat/api/navigation-examples/`, {});
            setNavigationExamples(response.data);
        } catch (error) {
            if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            } else {
                console.error('Error fetching navigation examples:', error);
            }
        }
    }
  
    const handleChatSelect = (chatId) => {
        if (chatId) {
            setSelectedChatId(chatId);
            fetchChatData(chatId);
            navigate(`/chat/${chatId}`)
        } else {
            const newId = uuidv4();
            setSelectedChatId(newId);
            setUtteranceFromApi([]);
            navigate(`/chat/${newId}`);
        }
        setShowMenu(false)
        setShowDashboard(false)
    }

    const closeSidebarClick = () => {
        setShowChatSidebar(false);
    }

    const displaySidebarClick = () => {
        setShowChatSidebar(true);
    }

    const onTitleChange = (renameChatId, renameChatTitle) => {
        setRenameChat({chatId: renameChatId, title: renameChatTitle});
        setShowRenameDialog(true);
    }

    const onDeleteChat = (deleteChatId, deleteTitle) => {
        setDeleteChat({chatId: deleteChatId, title: deleteTitle});
        setShowDeleteOkDialog(true);
    }

    const onDeleteCancelClick = () => {
        setShowDeleteOkDialog(false);
    }

    const onDeleteOkClick = async (deleteChatId) => {
        try {
            // DELETEリクエストを送信
            const response = await api.delete(`chat/api/chat/${deleteChatId}/`);

            if (response.status === 200) {
                await fetchChats(); // ノートリストをリフレッシュ
            } else {
                console.error("Failed to delete note");
            }
            setShowDeleteOkDialog(false);
        } catch (error) {
            console.error("Error deleting note:", error);
        }
    }

    const fetchChats = async () => {
        try {
            const response = await api.get(`${process.env.REACT_APP_API_URL}/chat/api/chat-list/`);
            setChats(response.data);
        } catch (error) {
          if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            // 未認証の場合、ログイン画面にリダイレクト
            // navigate('/login', { state: { from: location.pathname } });
          } else {
            console.error('Error fetching chat list:', error);
          }
        }
    };

    const renameOkClick = async (chatId, newTitle) => {
        try {
            // PATCHリクエストでタイトルを更新
            const response = await api.patch(`chat/api/chat/${chatId}/`, {
                title: newTitle
            });
    
            if (response.status === 200) {
                await fetchChats(); // チャットリストをリフレッシュ
            } else {
                console.error("Failed to update title");
            }
            setShowRenameDialog(false);
        } catch (error) {
            console.error("Error updating title:", error);
        }
    };
    
    const updateChatSidebar = (chatId, userMessage) => {
        const now = new Date().toISOString();  
        setChats((prev) => [{
           chat_id: chatId,
           title: userMessage.slice(0, 20),
           created_at: now,
           updated_at: now,
           user: null
        }, ...prev]);
        setSelectedChatId(chatId);
    }

    /* ダッシュボード */
    const closeDashboardClick = () => {
        setShowDashboard(false);
    };

    const handleDashboardProcess = (agentName) => {
        // setDashboardSecCode(agentName)
        dispatch({ type: 'SET_SEC_CODE', payload: agentName})
        setShowDashboard(true);
    }

    const mainMenuClick = () => {
        setShowMenu(!showMenu)
    }
    
    useEffect(() => {
        fetchChats();
        fetchNavigationExamples();
        
        document.addEventListener('click', handleDashboardLinkClick);
        return () => {
          document.removeEventListener('click', handleDashboardLinkClick);
        };
    }, []);
    
    if (!selectedChatId) {
        return <Loading />
    }

    return (
        <div className='w-full h-full overflow-y-scroll'>
            <MainMenu ref={mainMenuRef} handleClick={mainMenuClick} />
            <Suspense fallback={<Loading />}>
                <div className="main-container">
                    <div ref={sidebarRef}>
                        <Sidebar selectedIcon="chat" showMenu={showMenu} />
                    </div>
                    {showChatSidebar ? 
                        <ChatSidebar
                            ref={chatSidebarRef}
                            onChatSelect={handleChatSelect}
                            closeSidebarClick={closeSidebarClick}
                            onDeleteChat={onDeleteChat}
                            onTitleChange={onTitleChange}
                            chats={chats}
                            showMenu={showMenu}
                        />
                    :
                        <SidebarDisplayButton displaySidebarClick={displaySidebarClick}/>
                        }
                    <ChatContainer
                        initialChat={utteranceFromApi}
                        chatId={selectedChatId}
                        // key={selectedChatId}
                        expandMargin={!showChatSidebar}
                        navigationExamples={navigationExamples}
                        updateChatSidebar={updateChatSidebar}
                        setShowChatSidebar={setShowChatSidebar}
                        handleDashboardProcess={handleDashboardProcess}
                        showDashboard = {showDashboard}
                    />
                    {showDeleteOkDialog && <ConfirmDialog message={`「${deleteChat.title}」を削除してもよいですか？`} onOkClick={() => onDeleteOkClick(deleteChat.chatId)} onCancelClick={onDeleteCancelClick} />}
                    {showRenameDialog && <RenameDialog chatId={renameChat.chatId} title={renameChat.title} onOkClick={renameOkClick} onCancelClick={() => setShowRenameDialog(false)} /> }
                </div>
            </Suspense>
            {showDashboard && <DashboardModal chatId={selectedChatId} closeClickFunction={closeDashboardClick} />}
        </div >
    );
}

export default ChatPage;


const RenameDialog = ({chatId, title, onOkClick, onCancelClick}) => {
    const [newTitle, setNewTitle] = useState(title)

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            onOkClick(chatId, newTitle);
        }
    }
    
    return (
        <div  className="confirm-modal">
            <div className="confirm-modal-content">
                <p>新しいタイトルを入力してください</p>
                <input 
                type="text" 
                className="confirm-modal-input" 
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={handleKeyDown}
                />
                <div className="confirm-modal-buttons">
                    <button className="confirm-modal-button confirm-yes" onClick={() => onOkClick(chatId, newTitle)}>OK</button>
                    <button className="confirm-modal-button confirm-no" onClick={onCancelClick}>キャンセル</button>
                </div>
            </div>
        </div>
    )
  }
  